import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiPaths } from "../../../api/paths";
import DataTable from "../../../components/data-table/DataTable";
import Pagination from "../../../components/pagination/Pagination";
import WalletConnectButton from "../../../components/wallet-connect-button/WalletConnectButton";
import useData from "../../../hooks/use-data";
import usePagination from "../../../hooks/use-pagination";
import { PaginatedApiResponseType } from "../../../types/common";
import { CoverType } from "../../../types/cover";
import { ActionType, ColumnType } from "../../../types/data-table";
import { WalletType } from "../../../types/user";
import "./change-wallet-address.css";
import { ProductType } from "../../../types/product";
import { IMAGE_URL } from "../../../resources/constants";
import { BsArrowUpRight } from "react-icons/bs";
import CustomButton from "../../../components/custom-button/CustomButton";

export default function ChangeWalletAddress() {
  const { t } = useTranslation(["changeWalletAddress"]);
  const navigate = useNavigate();
  const pagination = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const { walletId } = Object.fromEntries(searchParams);
  const params = useMemo(
    () => ({
      params: {
        page: pagination.page,
        limit: pagination.limit,
        walletId,
      },
    }),
    [pagination.page, pagination.limit, walletId]
  );
  const { data, loading } = useData<PaginatedApiResponseType<CoverType[]>>(
    ApiPaths.covers,
    {
      options: params,
      onError: (err) => {
        toast(err.response?.data?.message || "An error occurred", {
          type: "error",
        });
      },
    }
  );
  const { data: walletData } = useData<PaginatedApiResponseType<WalletType[]>>(
    ApiPaths.wallets
  );
  const handleFilter = (key: string, value: string) => {
    pagination.handlePage(1);
    setSearchParams({ ...Object.fromEntries(searchParams), [key]: value });
  };

  const columns: ColumnType[] = [
    {
      accessor: "product_name",
      header: t("table_head.product_name", { ns: "myCovers" }),
      formatDisplay: (row: CoverType) => {
        return row?.product?.product_name ? (
          <div>{row?.product?.product_name}</div>
        ) : (
          "-"
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product_owner_name",
      header: t("table_head.product_owner_name", { ns: "myCovers" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover_category",
      header: t("table_head.product_type", { ns: "myCovers" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "currency",
      header: t("table_head.currency", { ns: "myCovers" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "period",
      header: t("table_head.period", { ns: "myCovers" }),
      formatDisplay: (row) => {
        return row.period ? <div>{row?.period + " days"}</div> : "-";
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover_wording",
      header: t("table_head.product_details", {
        ns: "myCovers",
      }),
      formatDisplay: (row: ProductType) => (
        <a
          href={`${IMAGE_URL}${row?.cover_wording}`}
          target="_blank"
          rel="noreferrer"
        >
          Download <BsArrowUpRight />
        </a>
      ),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover_status",
      header: t("table_head.status", { ns: "myCovers" }),
      formatDisplay: (row) => {
        return row.cover_status ? (
          <button
            className={`status-button px-4 py-2 ${row?.cover_status?.toLowerCase()}-status`}
          >
            {row?.cover_status}
          </button>
        ) : (
          "-"
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
  ];

  const actions: ActionType[] = [
    {
      name: "raise_new_claim",
      action: (row: CoverType) => {
        return (
          <CustomButton
            className="primary-button"
            onClick={() => {
              navigate(`/my-covers/${row?._id}/assetCover`);
            }}
          >
            {t("button.view_details", { ns: "myCovers" })}
          </CustomButton>
        );
      },
    },
  ];

  return (
    <div className="p-3 pb-5 p-md-4">
      <div className="change-wallet-cont p-3 p-md-0 mb-3 mb-md0">
        <h2 className="primary-title mb-3">
          {t("heading.wallet", { ns: "changeWalletAddress" })}
        </h2>
        <div className="position-relative d-flex flex-column flex-md-row align-items-center gap-4 gap-lg-3">
          <div className="existing-wallet rounded-2 bg-primary text-white position-relative mb-2 mb-md-0">
            <h6 className="font-12 text-white fw-normal mb-1">
              {t("label.existing", { ns: "changeWalletAddress" })}
            </h6>
            <select
              className="font-20 fw-bold wallet-select"
              id=""
              onChange={(e) => handleFilter("walletId", e.target.value)}
              value={walletId}
            >
              <option value="">Select a walletId</option>
              {walletData?.data?.map((wallet) => {
                return (
                  <option value={wallet.walletId} key={wallet._id}>
                    {wallet.walletId}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="box-shadow or-container bg-white p-3 p-md-2 p-lg-3 rounded-circle fw-bold font-15">
            <p className="m-0">{t("label.or", { ns: "common" })}</p>
          </div>
          <div className="scan-qr-container bg-white box-shadow d-flex align-items-center">
            <img
              src={process.env.PUBLIC_URL + "/assets/icons/qr-scanner-icon.png"}
              alt="QR-Scanner"
            />
            <WalletConnectButton className="primary-button px-md-3 py-md-2 mx-3 ms-md-0 mx-lg-3">
              {t("button.qr", { ns: "changeWalletAddress" })}
            </WalletConnectButton>
          </div>
        </div>
      </div>
      <div className=" mb-5">
        <h2 className="primary-title mb-3">
          {t("heading.policies", { ns: "changeWalletAddress" })}
        </h2>
        <div className="primary-table-cont">
          <DataTable
            columns={columns}
            data={data?.data}
            showSerial={true}
            actions={actions}
            loading={loading}
          />
        </div>
        <div className="mb-5">
          <Pagination total={data?.count ?? 10} pagination={pagination} />
        </div>
      </div>
    </div>
  );
}
